import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { AchievementBadge, ALL_ACHIEVEMENT_TYPES, type AchievementBadgeType } from "@/components/AchievementBadge";
import { useLocation } from "wouter";
import { useEffect } from "react";

interface Achievement {
  id: string;
  userId: string;
  achievementType: AchievementBadgeType;
  unlockedAt: string;
  metadata: any;
}

const allAchievements: AchievementBadgeType[] = ALL_ACHIEVEMENT_TYPES;

export function AchievementsPage() {
  const [, setLocation] = useLocation();

  const { data: achievements = [], refetch } = useQuery<Achievement[]>({
    queryKey: ["/api/achievements"],
    queryFn: async () => {
      const res = await fetch("/api/achievements", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  // Recalculate this user's achievements once per session.
  useEffect(() => {
    const backfillRun = sessionStorage.getItem("achievementsBackfillRun");
    if (!backfillRun) {
      sessionStorage.setItem("achievementsBackfillRun", "true");
      fetch("/api/achievements/backfill", { method: "POST", credentials: "include" })
        .then((r) => r.json())
        .then(() => {
          refetch();
        })
        .catch(() => {});
    }
  }, [refetch]);

  const unlockedTypes = new Set(achievements.map((a) => a.achievementType));
  const achievementMap = new Map(achievements.map((a) => [a.achievementType, a]));

  return (
    <div className="min-h-screen bg-background pb-6">
      <header 
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Logros</h1>
          <span className="ml-auto text-sm text-muted-foreground">
            {unlockedTypes.size} / {allAchievements.length}
          </span>
        </div>
      </header>

      <main className="container max-w-2xl mx-auto px-4 pt-6 safe-area-bottom">
        <Card className="mb-6" data-testid="card-achievements-summary">
          <CardHeader>
            <CardTitle className="text-base">Tu Progreso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${(unlockedTypes.size / allAchievements.length) * 100}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Has desbloqueado {unlockedTypes.size} de {allAchievements.length} logros
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="grid-achievements">
          {allAchievements.map((type) => (
            <AchievementBadge
              key={type}
              type={type}
              unlocked={unlockedTypes.has(type)}
              unlockedAt={
                achievementMap.get(type) ? new Date(achievementMap.get(type)!.unlockedAt) : undefined
              }
            />
          ))}
        </div>
      </main>
    </div>
  );
}
